import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {startTransition, useCallback, useEffect, useState} from 'react';
import {
  SessionGender,
  useSessionGender,
  useUpdateSessionGender,
} from './sessionGender';

export const useSelectedTargetGroup = (): {
  gender: SessionGender | undefined;
  // targetGroup with fallback to FEMALE if no sessionGender is set
  targetGroup: TargetGroup;
  // targetGroup with no fallback to FEMALE
  initialTargetGroup: TargetGroup | undefined;
  switchTargetGroup: (targetGroup: TargetGroup) => SessionGender | undefined;
} => {
  const {gender, updateGender} = useSessionGender();

  const switchTargetGroup = useCallback(
    (targetGroup: TargetGroup) => {
      const targetGender = convertTargetGroupToGender(targetGroup);
      if (targetGender) {
        updateGender(targetGender);
      }

      return targetGender;
    },
    [updateGender],
  );

  return {
    gender,
    targetGroup: convertGenderToTargetGroup({
      gender,
    }) as TargetGroup,
    initialTargetGroup: convertGenderToTargetGroup({
      gender,
      defaultToFemale: false,
    }),
    switchTargetGroup,
  };
};

export const useUpdateTargetGroup = (targetGroup: TargetGroup | undefined) => {
  const {targetGroup: selectedTargetGroup} = useSelectedTargetGroup();
  const [isTargetGroupUpdating, setIsTargetGroupUpdating] = useState(
    // For unisex products we don't need to update the target group.
    targetGroup !== TargetGroup.UNSPECIFIED,
  );
  const nextGender = convertTargetGroupToGender(targetGroup);
  useUpdateSessionGender(nextGender);

  const isHydrationDone = useHydrationDone();

  // Initially tracking is prevented in case we need to switch the target group.
  // Once the target group is updated, we can start tracking.
  useEffect(() => {
    if (!isHydrationDone) {
      return;
    }
    if (targetGroup === selectedTargetGroup) {
      startTransition(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setIsTargetGroupUpdating(false);
      });
    }
  }, [
    selectedTargetGroup,
    targetGroup,
    setIsTargetGroupUpdating,
    isHydrationDone,
  ]);

  return {isTargetGroupUpdating};
};

export const convertTargetGroupToGender = (
  targetGroup: TargetGroup | undefined,
) => {
  switch (targetGroup) {
    case TargetGroup.FEMALE:
      return 'female';
    case TargetGroup.MALE:
      return 'male';
    case TargetGroup.KIDS:
      return 'kids';
    default:
      return undefined;
  }
};

export const convertGenderToTargetGroup = ({
  gender,
  defaultToFemale = true,
}: {
  gender: string | undefined;
  defaultToFemale?: boolean;
}) => {
  switch (gender) {
    case 'kids':
      return TargetGroup.KIDS;
    case 'male':
      return TargetGroup.MALE;
    case 'female':
      return TargetGroup.FEMALE;
    default:
      return defaultToFemale ? TargetGroup.FEMALE : undefined;
  }
};
